import AwesomeDebouncePromise from 'awesome-debounce-promise';
import omit from 'lodash/omit';
import flatten from 'lodash/flatten';
import isNull from 'lodash/isNull';
import isArray from 'lodash/isArray';

import type { Optional } from 'chaika-common-types';
import type {
    MedicalCaseData,
    MedicalCaseCreateParams,
    MedicalCaseUpdateParams,
    ReferralCreateData,
    MedicalCaseHistoryQuery,
    RecommendationData,
    RecommendationCreationParams,
    RecommendationUpdateParams,
    OutwardReferralCreationParams,
    OutwardReferralUpdatingParams,
    OutwardReferralExecutionChangingParams,
    OutwardReferralData,
    MedicalCaseCloseDataUpdatingForm,
} from 'chaika-frontend-types/frontend-cms';
import { MedicalCaseStatus } from 'chaika-enums';

import { userService } from '@user/services/userService';
import { MedicalCaseApi } from '@api/MedicalCaseApi';
import { OutwardReferralApi } from '@api/OutwardReferralApi';
import { RecommendationApi } from '@api/RecommendationApi';
import { MedicalCaseList } from '@store/models/SystemFields/MedicalCase';
import { objectDifference } from '@utils/objectDifference';
import { isDefined } from '@utils/isDefined';
import { DrugParams } from '@components/Blocks';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { patientService } from '@patient/services/patient/patientService';
import { store } from '@store';
import { SYSTEM_USER_ID } from '@store/types';
import { priceService } from '@price/service/price/priceService';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { assertIsDefined } from '@utils/assertIsDefined';
import omitBy from 'lodash/omitBy';

export interface MedicalCaseTaskIdentity {
    medicalCaseId: string;
    taskId: string;
}

export interface MedicalCaseRecommendationIdentity {
    medicalCaseId: string;
    recommendationId: string;
}

export interface IMedicalCaseService {
    load(): void;
    create(params: MedicalCaseCreateParams): void;
    update(id: string, params: MedicalCaseUpdateParams): void;
    remove(id: string): void;
    attachToEntry(medicalCaseId: string): void;
    createRecommendation(
        medicalCaseId: string,
        recommendation: Omit<RecommendationCreationParams, 'medicalCaseIds'>,
    ): Promise<RecommendationData>;
    updateRecommendation(identity: MedicalCaseRecommendationIdentity, params: RecommendationUpdateParams): void;
    removeRecommendation(identity: MedicalCaseRecommendationIdentity): void;
    changeRecommendationImportance(identity: MedicalCaseRecommendationIdentity, isImportant: boolean): void;
    loadOutwardReferrals(medicalCaseIds: string[]): void;
    loadOutwardReferralForMedicalCases(medicalCaseIds: string[]): void;
    createOutwardReferral(
        medicalCaseId: string | string[],
        params: OutwardReferralCreationParams,
    ): Promise<OutwardReferralData>;
    updateOutwardReferral(outwardReferralId: string, params: OutwardReferralUpdatingParams): void;
    updateOutwardReferralStatus(outwardReferralId: string, params: OutwardReferralExecutionChangingParams): void;
    removeOutwardReferral(outwardReferralId: string): void;
    createReferral(ids: string[], referral: ReferralCreateData): void;
    createReferrals(referral: ReferralCreateData[]): void;
    attachReferral(medicalCaseId: string | string[], referralId: string, referringDoctorId: string): void;
    dettachReferral(medicalCaseId: string | string[], referralId: string, referringDoctorId: string): void;
    createDrug?(ids: string[], medicalCaseId: string, createParams: DrugParams): void;
    removeDrug?(medicalCaseId: string, drugId: string, parentEntryId: string): void;
    updateDrug?(drugId: string, drug: DrugParams, parentEntryId: string): void;
    updateShareLink(medicalCaseId: string, welcomeMessage: string): void;
}

export interface MedicalCaseBasicParams {
    id: string;
    authorId: string;
    managerId: string;
    ehrId: string;
}

export abstract class MedicalCaseService {
    private updateRecommendationApi = AwesomeDebouncePromise(RecommendationApi.update, 500);
    private updateOutwardReferralApi = AwesomeDebouncePromise(OutwardReferralApi.update, 500);

    public abstract load(): Promise<void>;
    public abstract create(params: MedicalCaseCreateParams): void;
    public abstract loadWithVersions(params?: Partial<MedicalCaseHistoryQuery>): Promise<void>;
    public abstract update(id: string, params: MedicalCaseUpdateParams): Promise<void>;

    public async loadMedicalCaseData(medicalCases: MedicalCaseData[]): Promise<void> {
        const userIds = new Set<string>();
        const ehrIds = new Set<string>();
        const icdIds = new Set<string>();

        medicalCases.forEach(({ authorId, diagnoses, managerId, ehrId, versionAuthorId }) => {
            userIds.add(authorId);
            userIds.add(managerId);
            ehrIds.add(ehrId);
            diagnoses.forEach(({ icdId }) => {
                icdIds.add(icdId);
            });

            if (versionAuthorId && versionAuthorId !== SYSTEM_USER_ID) {
                userIds.add(versionAuthorId);
            }
        });

        await Promise.all([
            dictionaryService.loadDictionaryItemsByIds(Array.from(icdIds.values())),
            userService.loadIfNotExist(Array.from(userIds.values())),
            patientService.loadByIds(Array.from(ehrIds.values())),
        ]);
    }

    public async loadBadMedicalCaseData(medicalCases: MedicalCaseBasicParams[]): Promise<void> {
        const userIds = new Set<string>();
        const ehrIds = new Set<string>();

        medicalCases.forEach(({ authorId, managerId, ehrId, id }) => {
            userIds.add(authorId);
            userIds.add(managerId);
            ehrIds.add(ehrId);
        });

        await Promise.all([
            userService.loadIfNotExist(Array.from(userIds.values())),
            patientService.loadByIds(Array.from(ehrIds.values())),
        ]);
    }

    protected async createMedicalCase(
        medicalCases: MedicalCaseList,
        params: MedicalCaseCreateParams & { description?: string },
    ): Promise<MedicalCaseData> {
        await MedicalCaseApi.create(omit(params, ['description']));
        const medicalCase = await MedicalCaseApi.getById(params.id);

        medicalCases.add([{ ...medicalCase, versionAuthorId: store.authUser.id }]);
        return medicalCase;
    }

    protected async updateMedicalCaseStatus(
        medicalCases: MedicalCaseList | MedicalCaseList[],
        id: string,
        status: MedicalCaseStatus,
        entryId?: string,
    ): Promise<void> {
        await MedicalCaseApi.updateStatus(id, status, entryId);
        if (isArray(medicalCases)) {
            medicalCases.forEach(medicalCaseListItem => {
                this.updateMedicalCaseStatusInList(id, status, medicalCaseListItem);
            });
        } else {
            this.updateMedicalCaseStatusInList(id, status, medicalCases);
        }
    }

    protected updateMedicalCaseStatusInList(
        id: string,
        status: MedicalCaseStatus,
        medicalCases: MedicalCaseList,
    ): void {
        const current = medicalCases.get(id);

        if (isDefined(current)) {
            medicalCases.addVersion(id, {
                ...current,
                ...current.dto,
                outwardReferralIds: current.outwardReferralIds,
            });
            const updateParams = {
                status,
                versionDate: new Date().toISOString(),
                versionAuthorId: store.authUser.id,
            } as MedicalCaseData;

            medicalCases.update(id, updateParams);

            if (isDefined(current.closeData) && status === MedicalCaseStatus.Open) {
                medicalCases.updateCloseData(id, undefined);
            }
        }
    }

    protected async updateMedicalCase(
        medicalCases: MedicalCaseList,
        id: string,
        params: MedicalCaseUpdateParams,
        entryId?: string,
    ): Promise<void> {
        const current = medicalCases.getExisting(id);
        const diff = omit(objectDifference(params, current), 'versionDate');

        await MedicalCaseApi.update(id, diff, entryId);

        medicalCases.addVersion(id, {
            ...current,
            ...current.dto,
            outwardReferralIds: current.outwardReferralIds,
        });
        medicalCases.update(id, {
            ...params,
            versionEntryId: isDefined(params.description) ? entryId : undefined,
            versionDate: new Date().toISOString(),
            versionAuthorId: store.authUser.id,
        });
    }

    protected async updateMedicalCaseCloseData(
        medicalCases: MedicalCaseList,
        id: string,
        closeData: MedicalCaseCloseDataUpdatingForm,
    ): Promise<void> {
        const current = medicalCases.getExisting(id);

        await MedicalCaseApi.updateCloseData(id, closeData);

        medicalCases.addVersion(id, {
            ...current,
            ...current.dto,
            outwardReferralIds: current.outwardReferralIds,
        });
        medicalCases.update(id, {
            versionEntryId: closeData.entryId,
            versionAuthorId: store.authUser.id,
            versionDate: new Date().toISOString(),
        });
        medicalCases.updateCloseData(id, closeData.closeData ?? undefined);
    }

    protected async updateMedicalCaseShareLink(
        medicalCases: MedicalCaseList,
        id: string,
        welcomeMessage: string,
    ): Promise<void> {
        const shareLink = await MedicalCaseApi.updateShareLink(id, welcomeMessage);
        medicalCases.update(id, { shareLink });
    }

    protected async removeMedicalCase(medicalCases: MedicalCaseList, id: string): Promise<void> {
        await MedicalCaseApi.remove(id);
        medicalCases.remove(id);
    }

    protected async loadMedicalCaseRecommendations(
        medicalCases: MedicalCaseList,
        medicalCaseIds: string[],
        actualOn?: Date,
    ): Promise<void> {
        const chunked = getUniqChunkedIds(medicalCaseIds);
        const recommendations = flatten(
            await Promise.all(
                chunked.map(ids => RecommendationApi.getHistory({ actualOn, medicalCaseIds: ids.join() })),
            ),
        );

        const groupped = new Map<string, RecommendationData[]>();
        recommendations.forEach(recommendation => {
            recommendation.medicalCaseIds.forEach((id: string) => {
                groupped.set(id, [...(groupped.get(id) ?? ([] as RecommendationData[])), recommendation]);
            });
        });

        Array.from(groupped.keys()).forEach((id: string) => {
            if (medicalCases.has(id)) {
                const medicalCaseRecommendations = groupped.get(id);
                assertIsDefined(medicalCaseRecommendations, `Recommendations are grouped by medical case ${id}`);
                medicalCases.setRecommendations(id, medicalCaseRecommendations);
            }
        });
    }

    protected async createMedicalCaseRecommendation(
        medicalCases: MedicalCaseList,
        medicalCaseId: string | string[],
        params: RecommendationCreationParams,
    ): Promise<RecommendationData> {
        await RecommendationApi.create(medicalCaseId, params);
        const recommendation = await RecommendationApi.getById(params.id);

        if (isArray(medicalCaseId)) {
            medicalCaseId.forEach(id => {
                medicalCases.addRecommendation(id, recommendation);
            });
        } else {
            medicalCases.addRecommendation(medicalCaseId, recommendation);
        }

        return recommendation;
    }

    protected async updateMedicalCaseRecommendation(
        medicalCases: MedicalCaseList,
        { medicalCaseId, recommendationId }: MedicalCaseRecommendationIdentity,
        params: RecommendationUpdateParams,
    ): Promise<void> {
        await this.updateRecommendationApi(medicalCaseId, recommendationId, params);

        medicalCases.updateRecommendation(medicalCaseId, recommendationId, params);
    }

    protected async updateMedicalCasesRecommendation(
        recommendationId: string,
        medicalCases: MedicalCaseList,
        params: RecommendationUpdateParams,
    ): Promise<void> {
        const medicalCasesWithRecommendations = medicalCases.getMedicalCasesByRecommendationId(recommendationId);
        const medicalCaseIds = medicalCasesWithRecommendations.map(({ id }) => id);
        await this.updateRecommendationApi(medicalCaseIds.join(), recommendationId, params);

        medicalCases.updateRecommendationFromMedicalCases(medicalCaseIds, recommendationId, params);
    }

    protected async updateMedicalCaseRecommendationImportance(
        medicalCases: MedicalCaseList,
        { medicalCaseId, recommendationId }: MedicalCaseRecommendationIdentity,
        isImportant: boolean,
        entryId?: string,
    ): Promise<void> {
        await RecommendationApi.updateImportance(medicalCaseId, recommendationId, isImportant, entryId);

        medicalCases.updateRecommendation(medicalCaseId, recommendationId, { isImportant });
    }

    protected async removeMedicalCaseRecommendation(
        medicalCases: MedicalCaseList,
        { medicalCaseId, recommendationId }: MedicalCaseRecommendationIdentity,
    ): Promise<void> {
        await RecommendationApi.remove(medicalCaseId, recommendationId);

        medicalCases.removeRecommendation(medicalCaseId, recommendationId);
    }

    protected async removeRecommendationMedicalCases(
        recommendationId: string,
        medicalCases: MedicalCaseList,
    ): Promise<void> {
        const medicalCasesWithRecommendations = medicalCases.getMedicalCasesByRecommendationId(recommendationId);
        const medicalCaseIds = medicalCasesWithRecommendations.map(({ id }) => id);

        await RecommendationApi.remove(medicalCaseIds.join(), recommendationId);

        medicalCases.removeRecommendationFromCases(medicalCaseIds, recommendationId);
    }

    protected async loadMedicalCaseOutwardReferral(
        medicalCases: MedicalCaseList,
        medicalCaseIds: string[],
    ): Promise<void> {
        const chunked = getUniqChunkedIds(medicalCaseIds);
        const outwardReferrals: OutwardReferralData[] = flatten<OutwardReferralData>(
            await Promise.all(chunked.map(ids => OutwardReferralApi.find({ medicalCaseId: ids.join(',') }))),
        );

        outwardReferrals.forEach(outwardReferral => {
            outwardReferral.medicalCaseIds.forEach(id => {
                medicalCases.addOutwardReferral(id, outwardReferral);
            });
        });
    }

    protected async createMedicalCaseOutwardReferral(
        medicalCases: MedicalCaseList,
        medicalCaseId: string | string[],
        params: OutwardReferralCreationParams,
    ): Promise<OutwardReferralData> {
        const medicalCaseIds = isArray(medicalCaseId) ? medicalCaseId : [medicalCaseId];
        await OutwardReferralApi.create(params);

        const outwardsReferral = {
            ...params,
            medicalCaseIds,
            authorId: store.authUser.id,
            createdAt: new Date().toISOString(),
            isExecuted: false,
        } as OutwardReferralData;

        medicalCaseIds.forEach(medicalCaseId => medicalCases.addOutwardReferral(medicalCaseId, outwardsReferral));

        return outwardsReferral;
    }

    protected async updateMedicalCaseOutwardReferral(
        medicalCases: MedicalCaseList,
        outwardReferralId: string,
        params: OutwardReferralUpdatingParams,
    ): Promise<void> {
        const outwardReferral = medicalCases.getOutwardReferral(outwardReferralId);

        const updateParams = omitBy(params, (value, key) => {
            return outwardReferral?.[key as keyof typeof outwardReferral] === value;
        });

        await this.updateOutwardReferralApi(outwardReferralId, updateParams);

        medicalCases.updateOutwardReferral(outwardReferralId, updateParams);
    }

    protected async updateMedicalCaseOutwardReferralStatus(
        medicalCases: MedicalCaseList,
        outwardReferralId: string,
        params: OutwardReferralExecutionChangingParams,
    ): Promise<void> {
        await OutwardReferralApi.changIsExecuted(outwardReferralId, params.isExecuted, params.executionEntryId);

        medicalCases.updateOutwardReferralExecuted(outwardReferralId, params);
    }

    protected async removeMedicalCaseOutwardReferral(
        medicalCases: MedicalCaseList,
        outwardReferralId: string,
    ): Promise<void> {
        await OutwardReferralApi.remove(outwardReferralId);

        medicalCases.removeOutwardReferral(outwardReferralId);
    }

    protected addReferral(medicalCases: MedicalCaseList, { medicalCaseIds, id }: ReferralCreateData): void {
        medicalCaseIds.forEach(medicalCaseId => {
            medicalCases.addReferral(medicalCaseId, id);
        });
    }

    protected addReferrals(medicalCases: MedicalCaseList, referrals: ReferralCreateData[]): void {
        referrals.forEach(({ medicalCaseIds, id }) => {
            medicalCaseIds.forEach(medicalCaseId => {
                medicalCases.addReferral(medicalCaseId, id);
            });
        });
    }

    protected removeReferral(medicalCases: MedicalCaseList, { medicalCaseIds, id }: ReferralCreateData): void {
        medicalCaseIds.forEach(medicalCaseId => {
            medicalCases.removeReferral(medicalCaseId, id);
        });
    }

    protected async setOrganizationIdToCreateReferralParams({
        organizationId,
        ...referral
    }: ReferralCreateData): Promise<ReferralCreateData> {
        const { authUser } = store;

        const defaultOrganizationId = !isNull(organizationId)
            ? (organizationId ?? !!authUser.organizationId)
                ? await this.getDefaultOrganization(referral.serviceId)
                : undefined
            : undefined;

        return {
            ...referral,
            organizationId: defaultOrganizationId,
        };
    }

    private async getDefaultOrganization(serviceId: string): Promise<Optional<string>> {
        const { organizationId } = store.authUser;

        const hasPrices = !!organizationId
            ? await this.hasPricesForLegalEntityByOrganizationId(organizationId, serviceId)
            : [];

        return hasPrices ? organizationId : undefined;
    }

    private async hasPricesForLegalEntityByOrganizationId(organizationId: string, serviceId: string): Promise<boolean> {
        return priceService.hasPricesForLegalEntityByOrganizationId(organizationId, serviceId);
    }
}
